import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { V2_BASE } from '../config';
import { ArrowRightRegular, ServerRegular } from '@fluentui/react-icons';
import './HomePage.css';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtEuro = (n) => '€ ' + Number(n || 0).toFixed(2).replace('.', ',');
const greeting = (h) => (h < 12 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera');

// Settore del servizio (per colore tile) dalla categoria/tipo
const sectorOf = (s) => {
  const t = `${s.categoria || ''} ${s.tipo_prodotto || ''} ${s.nome || ''}`.toLowerCase();
  if (/host|dominio|domain|sito|web|ssl/.test(t)) return 'hosting';
  if (/pec|spid|firma|trust|marca|fattur/.test(t)) return 'pec';
  if (/vps|cloud|server|dedic|nas|storage|backup|housing/.test(t)) return 'cloud';
  return '';
};

// Icone inline (lucide-like) per i widget
const Ico = {
  arrow: (p) => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M5 12h14M13 5l7 7-7 7" /></svg>),
  dots: (p) => (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></svg>),
  help: (p) => (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M9.2 9a2.8 2.8 0 0 1 5.4 1c0 2-2.8 2.5-2.8 2.5M12 17h.01" /></svg>),
  shield: (p) => (<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6Z" /></svg>),
  plus: (p) => (<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><path d="M12 5v14M5 12h14" /></svg>),
  pulse: (p) => (<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 12h4l3 8 4-16 3 8h4" /></svg>),
  ticket: (p) => (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4Z" /></svg>),
  roadmap: (p) => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 19V5l8 4 8-4v14l-8 4-8-4Z" /><path d="M12 9v14" /></svg>),
  book: (p) => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2Z" /><path d="M8 7h7M8 11h7" /></svg>),
};

const HomePage = () => {
  const { utente } = useAuth();
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);

  const [profilo, setProfilo] = useState(null);
  const [servizi, setServizi] = useState([]);
  const [insoluti, setInsoluti] = useState([]);
  const [sicurezza, setSicurezza] = useState(null);
  const [ordini, setOrdini] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    const api = axios.create({ withCredentials: true });
    api.get('/api/profilo').then(r => setProfilo(r.data)).catch(() => {});
    api.get('/api/servizi/riepilogo').then(r => setServizi(r.data?.servizi || [])).catch(() => {});
    api.get('/api/fatturazione/insoluti').then(r => setInsoluti(r.data?.insoluti || [])).catch(() => {});
    api.get('/api/sicurezza').then(r => setSicurezza(r.data)).catch(() => setSicurezza(null));
    api.get('/api/fatturazione/ordini')
      .then(r => setOrdini(r.data?.ordini || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => {});
    api.get('/api/support/tickets')
      .then(r => setTickets(r.data?.tickets || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => {});
  }, []);

  const nome = profilo
    ? (profilo.nome || profilo.nome_completo?.split(' ')[0] || utente?.email)
    : (utente?.nome_completo?.split(' ')[0] || utente?.email || '');
  const hour = new Date().getHours();
  const securityRisk = sicurezza ? !sicurezza.autenticazione_due_fattori : false;
  const nDaPagare = insoluti.length;
  const totDaPagare = insoluti.reduce((s, i) => s + Number(i.importo || 0), 0);
  const lastOrder = ordini[0] || null;

  return (
    <div className="nfx-home nfx-home--bento">
      {/* ══ HERO ══ */}
      <section className="nfx-hero">
        <div className="nfx-hero-links">
          <a className="nfx-hero-link" href="https://neoflux.it" target="_blank" rel="noreferrer">
            <Ico.roadmap />Roadmap &amp; changelog
          </a>
          <button className="nfx-hero-link" onClick={() => go('/support')}><Ico.book />Guide</button>
        </div>
        <h1 className="nfx-hero-title">{greeting(hour)}{nome ? <>, <strong>{nome}</strong></> : ''}!</h1>
        <p className="nfx-hero-sub">Visione d'insieme della tua attività</p>
        {(nDaPagare > 0 || securityRisk) && (
          <div className="nfx-hero-chips">
            {nDaPagare > 0 && (
              <button className="nfx-chip nfx-chip--danger" onClick={() => go('/billing/da-pagare')}>
                {nDaPagare} {nDaPagare === 1 ? 'prodotto da pagare' : 'prodotti da pagare'}
              </button>
            )}
            {securityRisk && (
              <button className="nfx-chip nfx-chip--warn" onClick={() => go('/account/sicurezza')}>
                Attiva la verifica in due fasi
              </button>
            )}
          </div>
        )}
      </section>

      {/* ══ BENTO ══ */}
      <div className="nfx-bento">
        {/* colonna larga */}
        <div className="nfx-bento-col">
          <section className="nfx-wcard">
            <div className="nfx-wcard-h">
              <h2>Ultimi stati di pagamento dei servizi</h2>
              {servizi.length > 0 && <span className="nfx-wnum">{servizi.length}</span>}
              <span className="nfx-sp" />
              <button className="nfx-seeall" onClick={() => go('/billing/rinnovo')}>Visualizza tutto <Ico.arrow /></button>
            </div>
            {servizi.length > 0 ? (
              <ul className="nfx-slist">
                {servizi.slice(0, 5).map((s, i) => {
                  const sec = sectorOf(s);
                  const auto = s.rinnovo === 'automatico';
                  return (
                    <li key={s.id || i}>
                      <span className={`nfx-slist-ic${sec ? ' sec-' + sec : ''}`}><ServerRegular /></span>
                      <span className="nfx-slist-meta">
                        <span className="nfx-slist-nm">{s.nome || s.nome_prodotto || '—'}</span>
                        <span className="nfx-slist-ty">{s.categoria || s.tipo_prodotto || 'Servizio'}</span>
                      </span>
                      <span className="nfx-slist-right">
                        <span className={`nfx-wtag ${auto ? 'nfx-wtag--ok' : 'nfx-wtag--warn'}`}>{auto ? 'Rinnovo automatico' : 'Rinnovo manuale'}</span>
                        <span className="nfx-slist-when">scad. {fmtDate(s.data_scadenza)}</span>
                      </span>
                      <button className="nfx-wdots" aria-label="Azioni"><Ico.dots /></button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="nfx-wempty"><div className="nfx-wempty-t">Nessun servizio attivo</div></div>
            )}
          </section>

          <section className="nfx-wcard">
            <div className="nfx-wcard-h">
              <h2>Ultimi ticket</h2>
              <span className="nfx-sp" />
              <button className="nfx-seeall" onClick={() => go('/support')}>Visualizza tutto <Ico.arrow /></button>
            </div>
            {tickets.length > 0 ? (
              <ul className="nfx-slist">
                {tickets.slice(0, 4).map((tk, i) => (
                  <li key={tk.id || i}>
                    <span className="nfx-slist-ic"><Ico.ticket /></span>
                    <span className="nfx-slist-meta">
                      <span className="nfx-slist-nm">{tk.titolo || tk.oggetto || `Ticket ${tk.numero || ''}`}</span>
                      <span className="nfx-slist-ty">{tk.numero ? `N° ${tk.numero}` : ''} {fmtDate(tk.creato_il || tk.created_at)}</span>
                    </span>
                    {tk.stato && <span className="nfx-wtag nfx-wtag--info">{tk.stato}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="nfx-wempty nfx-wempty--big">
                <span className="nfx-wempty-ic"><Ico.help /></span>
                <div className="nfx-wempty-t">Hai bisogno di aiuto?</div>
                <div className="nfx-wempty-s">Hai difficoltà nell'utilizzo di prodotti e servizi? Consulta le guide disponibili.</div>
                <button className="nfx-seeall" onClick={() => go('/support')}>Vai all'assistenza <Ico.arrow /></button>
              </div>
            )}
          </section>
        </div>

        {/* colonna stretta */}
        <div className="nfx-bento-col">
          <section className="nfx-wcard">
            <div className="nfx-wcard-h"><h2>Da saldare</h2></div>
            <div className="nfx-wbody">
              <div className="nfx-amount">{fmtEuro(totDaPagare)}</div>
              <div className="nfx-amount-sub">
                {nDaPagare > 0 ? `${nDaPagare} element${nDaPagare === 1 ? 'o' : 'i'} da pagare` : 'Nessun insoluto in questo momento'}
              </div>
              {nDaPagare > 0
                ? <button className="nfx-seeall" onClick={() => go('/billing/da-pagare')}>Vai al pagamento <Ico.arrow /></button>
                : <button className="nfx-seeall" onClick={() => go('/billing/fatture')}>Visualizza le fatture <Ico.arrow /></button>}
            </div>
          </section>

          <section className="nfx-wcard">
            <div className="nfx-wcard-h">
              <h2>Ultimo ordine</h2>
              <span className="nfx-sp" />
              <button className="nfx-seeall" onClick={() => go('/billing/ordini')}>I tuoi ordini <Ico.arrow /></button>
            </div>
            <div className="nfx-wbody">
              {lastOrder ? (
                <div className="nfx-order-row">
                  <div className="nfx-order-id">
                    <div className="nfx-onum">N° {lastOrder.numero_ordine || lastOrder.id || '—'}</div>
                    <div className="nfx-odate">{fmtDate(lastOrder.created_at || lastOrder.data_creazione)}</div>
                  </div>
                  {lastOrder.stato && <span className="nfx-wtag nfx-wtag--ok">{lastOrder.stato}</span>}
                </div>
              ) : (
                <div className="nfx-muted">Nessun ordine recente</div>
              )}
            </div>
          </section>

          <section className="nfx-wcard">
            <div className="nfx-wcard-h"><h2>Supporto NeoFlux</h2></div>
            <div className="nfx-wbody">
              <div className="nfx-suplevel"><Ico.shield /> Livello di supporto attuale: <b>Standard</b></div>
              <div className="nfx-suptiles">
                <button className="nfx-suptile" onClick={() => go('/support')}><span className="nfx-suptile-ic"><Ico.help /></span>Centro assistenza</button>
                <button className="nfx-suptile" onClick={() => go('/support')}><span className="nfx-suptile-ic"><Ico.plus /></span>Apri un ticket</button>
                <button className="nfx-suptile nfx-suptile--full" onClick={() => go('/support')}><span className="nfx-suptile-ic"><Ico.pulse /></span>Stato dei servizi</button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
